import { useRef, useState, type DragEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Download, FileSpreadsheet, Loader2, Upload, X } from 'lucide-react';
import type { TrackerFdfcImportResult } from '@tracker/shared';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const MAX_BYTES = 5 * 1024 * 1024;

function formatSize(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Client-side guard only; the backend re-checks type, size and content. */
function checkFile(file: File): string | null {
  if (!file.name.toLowerCase().endsWith('.xlsx')) return 'Choose an Excel .xlsx file.';
  if (file.size > MAX_BYTES) return 'File is larger than 5 MB.';
  return null;
}

export function TrackerFdfcImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState<TrackerFdfcImportResult | null>(null);
  const [downloading, setDownloading] = useState(false);

  const importMutation = useMutation({
    mutationFn: (f: File) => trackerFdfcService.importXlsx(f),
    onSuccess: (r) => {
      setResult(r);
      void queryClient.invalidateQueries({ queryKey: ['tracker-fdfc'] });
      const msg = `Imported ${r.imported_rows} of ${r.total_rows} rows`;
      if (r.failed) toast.warning(`${msg} — ${r.failed} failed`);
      else toast.success(msg);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pick = (f: File | undefined) => {
    if (!f) return;
    const err = checkFile(f);
    setFileError(err);
    setFile(err ? null : f);
  };

  const reset = () => {
    setFile(null);
    setFileError(null);
    setResult(null);
    importMutation.reset();
    if (inputRef.current) inputRef.current.value = '';
  };

  const close = () => {
    if (importMutation.isPending) return;
    reset();
    onClose();
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    pick(e.dataTransfer.files[0]);
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      await trackerFdfcService.downloadTemplate();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Import Data</DialogTitle>
          <DialogDescription>
            Upload an Excel sheet to add or update FDFC Tracker entries.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 rounded-lg border border-border/60 bg-muted/30 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <p className="text-muted-foreground">
                First row = headers. <span className="font-medium text-foreground">Mobile Number</span> is
                required; an existing mobile is updated, blank cells keep old values.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => void downloadTemplate()}
                disabled={downloading}
              >
                <Download className="h-4 w-4" /> {downloading ? 'Downloading…' : 'Download Template'}
              </Button>
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors',
                dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60 hover:bg-muted/30',
              )}
            >
              <Upload className="h-7 w-7 text-primary" />
              <p className="text-sm font-medium">Drop your Excel file here, or click to browse</p>
              <p className="text-xs text-muted-foreground">.xlsx only · up to 5 MB · max 5,000 rows</p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => pick(e.target.files?.[0])}
              />
            </div>

            {fileError && <p className="text-sm text-rose-600 dark:text-rose-400">{fileError}</p>}

            {file && (
              <div className="flex items-center gap-3 rounded-lg border border-border/60 p-3">
                <FileSpreadsheet className="h-8 w-8 shrink-0 text-emerald-600" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatSize(file.size)}</p>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="Remove file"
                  onClick={reset}
                  disabled={importMutation.isPending}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={close} disabled={importMutation.isPending}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => { if (file) importMutation.mutate(file); }}
                disabled={!file || importMutation.isPending}
              >
                {importMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Importing…</>
                ) : (
                  <><Upload className="h-4 w-4" /> Import</>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <ImportSummary result={result} onAgain={reset} onDone={close} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ImportSummary({
  result,
  onAgain,
  onDone,
}: {
  result: TrackerFdfcImportResult;
  onAgain: () => void;
  onDone: () => void;
}) {
  const tiles = [
    { label: 'New entries', value: result.created, className: 'text-emerald-600 dark:text-emerald-400' },
    { label: 'Updated', value: result.updated, className: 'text-primary' },
    { label: 'Failed rows', value: result.failed, className: result.failed ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground' },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {result.imported_rows} of {result.total_rows} rows imported.
      </p>
      <div className="grid grid-cols-3 gap-2">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-lg border border-border/60 p-3 text-center">
            <p className={cn('text-2xl font-semibold tabular-nums', t.className)}>{t.value}</p>
            <p className="text-xs text-muted-foreground">{t.label}</p>
          </div>
        ))}
      </div>

      {result.errors.length > 0 && (
        <div>
          <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Rows not imported
          </h3>
          <div className="max-h-60 overflow-auto rounded-lg border border-border/60">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Row</th>
                  <th className="px-3 py-2 text-left font-medium">Mobile</th>
                  <th className="px-3 py-2 text-left font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {result.errors.map((e) => (
                  <tr key={e.row} className="border-t border-border/40 align-top">
                    <td className="px-3 py-1.5 tabular-nums">{e.row}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{e.mobile ?? '—'}</td>
                    <td className="px-3 py-1.5 text-xs">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onAgain}>Import another file</Button>
        <Button type="button" onClick={onDone}>Done</Button>
      </DialogFooter>
    </div>
  );
}
